import i18n from 'i18next'
import dict from '@/assets/dictionaries.json'
import { initReactI18next } from 'react-i18next'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter as Router } from 'react-router-dom'
import { AppClientProvider } from './contexts/Services'
import { queryClient } from './lib/reactQuery'
import { client } from './services'
import { AuthProvider } from './contexts/Auth'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { createAppTheme } from './theme'
import AppRoutes from './routes/AppRoutes'
import { MaintenanceOverlay } from './components/MaintenanceOverlay'
import { ToastProvider } from './contexts/Toast'
import { ModalProvider } from './contexts/Modal'
import { ColorModeProvider, useColorMode } from './contexts/AppContext'
import { NavigationTrailProvider } from './contexts/NavigationTrail'
import { useMemo } from 'react'
import '@fontsource/anuphan'

function processDictionary(obj: Record<string, any>, index: number): Record<string, any> {
  return Object.entries(obj).reduce<Record<string, any>>((acc, [key, value]) => {
    if (Array.isArray(value)) {
      acc[key] = (value as unknown[])[index] || ''
    } else if (typeof value === 'object' && value !== null) {
      acc[key] = processDictionary(value as Record<string, any>, index)
    }
    return acc
  }, {})
}

const savedLanguage = localStorage.getItem('appLanguage') ?? 'en'

void i18n.use(initReactI18next).init({
  resources: {
    en: {
      translation: processDictionary(dict, 0),
    },
    th: {
      translation: processDictionary(dict, 1),
    },
  },
  lng: savedLanguage,
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
})

function AppWithTheme() {
  const { mode } = useColorMode()
  const theme = useMemo(() => createAppTheme(mode), [mode])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <AuthProvider>
          <Router>
            <NavigationTrailProvider>
              <ModalProvider>
                <AppRoutes />
                <MaintenanceOverlay />
              </ModalProvider>
            </NavigationTrailProvider>
          </Router>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppClientProvider client={client}>
        <ColorModeProvider>
          <AppWithTheme />
        </ColorModeProvider>
      </AppClientProvider>
    </QueryClientProvider>
  )
}

export default App
